import { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { 
  X, 
  Cookie, 
  Shield, 
  Settings, 
  BarChart3, 
  Megaphone, 
  Check, 
  ChevronDown,
  AlertCircle,
  Lock
} from 'lucide-react'
import { Link } from 'react-router-dom'
import { useCookieConsent } from '../context/CookieConsentContext'

const cookieCategories = [
  {
    id: 'essential',
    name: 'Essential Cookies',
    icon: Shield,
    required: true,
    description: 'These cookies are necessary for the website to function and cannot be disabled. They are usually set in response to actions you take, such as setting your privacy preferences, logging in, or filling in forms.',
    details: [
      'Session management and authentication',
      'Security features and fraud prevention',
      'Load balancing and server optimization',
      'Remembering your cookie consent preferences',
    ],
    examples: ['session_id', 'csrf_token', 'cookie_consent'],
  },
  {
    id: 'functional',
    name: 'Functional Cookies',
    icon: Settings,
    required: false,
    description: 'These cookies enable enhanced functionality and personalization, such as remembering your preferences, language settings, and customized features. If you disable these cookies, some or all of these services may not function properly.',
    details: [
      'Language and region preferences',
      'User interface customization',
      'Remembering form data and settings',
      'Chat and support widget preferences',
    ],
    examples: ['language', 'theme_preference', 'ui_settings'],
  },
  {
    id: 'analytics',
    name: 'Analytics Cookies',
    icon: BarChart3,
    required: false,
    description: 'These cookies help us understand how visitors interact with our website by collecting and reporting information anonymously. This data helps us improve our services, identify issues, and optimize user experience.',
    details: [
      'Page views and navigation patterns',
      'Feature usage and engagement metrics',
      'Performance and load time monitoring',
      'Error tracking and debugging',
    ],
    examples: ['_ga', '_gid', 'amplitude_id'],
  },
  {
    id: 'marketing',
    name: 'Marketing Cookies',
    icon: Megaphone,
    required: false,
    description: 'These cookies are used to track visitors across websites to display relevant advertisements. They may be set by us or by third-party advertising partners to build a profile of your interests and show you relevant ads on other sites.',
    details: [
      'Targeted advertising and retargeting',
      'Social media sharing features',
      'Conversion tracking for campaigns',
      'Cross-site user identification',
    ],
    examples: ['_fbp', 'ads_session', 'utm_source'],
  },
]

function CategoryToggle({ category, enabled, onChange, expanded, onToggleExpand }) {
  const Icon = category.icon
  
  return (
    <div className="border border-void-800/50 rounded-xl overflow-hidden">
      {/* Category Header */}
      <div 
        className={`p-4 transition-colors ${
          expanded ? 'bg-void-800/30' : 'bg-void-900/30 hover:bg-void-800/20'
        }`}
      >
        <div className="flex items-center justify-between">
          <button
            onClick={onToggleExpand}
            className="flex items-center gap-3 flex-1 text-left"
          >
            <div className={`p-2 rounded-lg ${
              category.required 
                ? 'bg-emerald-500/20 text-emerald-400' 
                : enabled 
                  ? 'bg-synth-500/20 text-synth-400'
                  : 'bg-void-800/50 text-void-500'
            }`}>
              <Icon className="w-5 h-5" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className="font-medium text-void-100">{category.name}</span>
                {category.required && (
                  <span className="px-2 py-0.5 rounded text-[10px] font-medium uppercase tracking-wider bg-emerald-500/20 text-emerald-400 border border-emerald-500/30">
                    Required
                  </span>
                )}
              </div>
              <p className="text-xs text-void-500 mt-0.5 line-clamp-1">
                {category.description.slice(0, 80)}...
              </p>
            </div>
            <ChevronDown className={`w-5 h-5 text-void-500 transition-transform ${expanded ? 'rotate-180' : ''}`} />
          </button>
          
          {/* Toggle Switch */}
          <div className="ml-4">
            {category.required ? (
              <div className="flex items-center gap-2 text-void-500">
                <Lock className="w-4 h-4" />
                <span className="text-xs">Always On</span>
              </div>
            ) : (
              <button
                onClick={() => onChange(!enabled)}
                className={`relative w-12 h-6 rounded-full transition-colors ${
                  enabled ? 'bg-synth-600' : 'bg-void-700'
                }`}
              >
                <motion.div
                  className="absolute top-1 w-4 h-4 rounded-full bg-white shadow-md"
                  animate={{ left: enabled ? '28px' : '4px' }}
                  transition={{ type: 'spring', stiffness: 500, damping: 30 }}
                />
              </button>
            )}
          </div>
        </div>
      </div>
      
      {/* Expanded Details */}
      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="p-4 pt-0 border-t border-void-800/50 bg-void-950/30">
              <p className="text-sm text-void-400 leading-relaxed mb-4">
                {category.description}
              </p>
              
              <div className="mb-4">
                <h5 className="text-xs font-medium text-void-300 uppercase tracking-wider mb-2">
                  What these cookies do:
                </h5>
                <ul className="space-y-1.5">
                  {category.details.map((detail, index) => (
                    <li key={index} className="flex items-start gap-2 text-sm text-void-400">
                      <Check className="w-4 h-4 text-synth-500 shrink-0 mt-0.5" />
                      {detail}
                    </li>
                  ))}
                </ul>
              </div>
              
              <div>
                <h5 className="text-xs font-medium text-void-300 uppercase tracking-wider mb-2">
                  Example cookies:
                </h5>
                <div className="flex flex-wrap gap-2">
                  {category.examples.map((example) => (
                    <span 
                      key={example}
                      className="px-2 py-1 rounded bg-void-800/50 text-void-500 text-xs font-mono"
                    >
                      {example}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

export default function CookiePreferencesModal() {
  const { 
    showPreferences, 
    closePreferences, 
    preferences, 
    savePreferences,
    acceptAll,
    acceptEssentialOnly 
  } = useCookieConsent()
  
  const [localPreferences, setLocalPreferences] = useState(preferences)
  const [expandedCategory, setExpandedCategory] = useState(null)
  
  useEffect(() => {
    setLocalPreferences(preferences)
  }, [preferences])

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (showPreferences) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }
    return () => {
      document.body.style.overflow = ''
    }
  }, [showPreferences])

  const handleToggle = (categoryId, value) => {
    setLocalPreferences(prev => ({
      ...prev,
      [categoryId]: value,
    }))
  }

  const handleSave = () => {
    savePreferences(localPreferences)
  }

  return (
    <AnimatePresence>
      {showPreferences && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[110] flex items-center justify-center p-4"
        >
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 bg-void-950/80 backdrop-blur-sm"
            onClick={closePreferences}
          />
          
          {/* Modal */}
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.95, opacity: 0 }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            className="relative w-full max-w-2xl max-h-[90vh] bg-void-900 rounded-2xl border border-void-700/50 shadow-2xl shadow-void-950/80 overflow-hidden flex flex-col"
          >
            {/* Header */}
            <div className="p-6 border-b border-void-800/50 shrink-0">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2.5 rounded-xl bg-synth-900/50 border border-synth-700/30">
                    <Cookie className="w-6 h-6 text-synth-400" />
                  </div>
                  <div>
                    <h2 className="text-xl font-display font-bold text-void-50">
                      Cookie Preferences
                    </h2>
                    <p className="text-sm text-void-400">
                      Manage your privacy settings
                    </p>
                  </div>
                </div>
                <button
                  onClick={closePreferences}
                  className="p-2 rounded-lg text-void-500 hover:text-void-300 hover:bg-void-800/50 transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            
            {/* Content */}
            <div className="flex-1 overflow-y-auto p-6">
              {/* Compliance Notice */}
              <div className="p-4 rounded-xl bg-synth-900/20 border border-synth-800/30 mb-6">
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-synth-400 shrink-0 mt-0.5" />
                  <div>
                    <h4 className="font-medium text-void-200 text-sm mb-1">
                      Your Privacy Rights
                    </h4>
                    <p className="text-xs text-void-400 leading-relaxed">
                      Under the California Consumer Privacy Act (CCPA), California Privacy Rights Act (CPRA), 
                      and General Data Protection Regulation (GDPR), you have the right to know what personal 
                      information is collected, used, and shared. You may opt out of the sale or sharing of 
                      your personal information at any time. SynthetIQ Signals®, Inc. does not sell your personal information.
                    </p>
                  </div>
                </div>
              </div>
              
              {/* Cookie Categories */}
              <div className="space-y-3">
                {cookieCategories.map((category) => (
                  <CategoryToggle
                    key={category.id}
                    category={category}
                    enabled={localPreferences[category.id]}
                    onChange={(value) => handleToggle(category.id, value)}
                    expanded={expandedCategory === category.id}
                    onToggleExpand={() => setExpandedCategory(
                      expandedCategory === category.id ? null : category.id
                    )}
                  />
                ))}
              </div>
              
              {/* Additional Info */}
              <div className="mt-6 p-4 rounded-xl bg-void-950/50 border border-void-800/30">
                <h4 className="font-medium text-void-200 text-sm mb-2">
                  About Our Data Practices
                </h4>
                <p className="text-xs text-void-400 leading-relaxed mb-3">
                  SynthetIQ Signals® and its AI-powered services may process data to provide, improve, 
                  and personalize our offerings. Our machine learning systems are designed with privacy 
                  in mind and operate under strict data governance policies.
                </p>
                <div className="flex flex-wrap gap-3">
                  <Link 
                    to="/corporate/legal/privacy"
                    onClick={closePreferences}
                    className="text-xs text-synth-400 hover:text-synth-300 transition-colors"
                  >
                    Read our Privacy Policy →
                  </Link>
                  <Link
                    to="/corporate/legal/cookies"
                    onClick={closePreferences}
                    className="text-xs text-synth-400 hover:text-synth-300 transition-colors"
                  >
                    Cookie Policy →
                  </Link>
                  <Link 
                    to="/support/contact"
                    onClick={closePreferences}
                    className="text-xs text-synth-400 hover:text-synth-300 transition-colors"
                  >
                    Contact Us →
                  </Link>
                </div>
              </div>
            </div>
            
            {/* Footer Actions */}
            <div className="p-6 border-t border-void-800/50 bg-void-950/50 shrink-0">
              <div className="flex flex-col sm:flex-row gap-3">
                <button
                  onClick={acceptEssentialOnly}
                  className="flex-1 px-4 py-2.5 text-sm font-medium text-void-400 hover:text-void-200 hover:bg-void-800/50 rounded-xl transition-colors"
                >
                  Essential Only
                </button>
                <button
                  onClick={handleSave}
                  className="flex-1 px-4 py-2.5 text-sm font-medium bg-void-800/50 text-void-200 hover:bg-void-700/50 rounded-xl border border-void-700/50 transition-colors"
                >
                  Save My Preferences
                </button>
                <button
                  onClick={acceptAll}
                  className="flex-1 px-4 py-2.5 text-sm font-semibold bg-gradient-to-r from-synth-600 to-synth-700 text-white rounded-xl hover:from-synth-500 hover:to-synth-600 transition-colors"
                >
                  Accept All
                </button>
              </div>
              
              <p className="text-center text-[10px] text-void-600 mt-4">
                You can change your preferences at any time by clicking the cookie icon in the footer.
              </p>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}